import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  VStack,
  HStack,
  Badge,
  Icon,
  Spinner,
  useColorModeValue,
  Divider,
  Tooltip,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  Input,
  Select,
  FormControl,
  FormLabel,
  FormHelperText,
  InputGroup,
  InputLeftElement,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Collapse,
  useDisclosure,
  Progress,
  List,
  ListItem,
  ListIcon,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import {
  FaDollarSign,
  FaChartPie,
  FaChartLine,
  FaChartBar,
  FaInfoCircle,
  FaStar,
  FaGlobe,
  FaBullhorn,
  FaUsers,
  FaTrophy,
  FaExclamationTriangle,
  FaLightbulb,
  FaCheckCircle,
  FaCalculator,
  FaFlagCheckered,
  FaClipboardList,
  FaShieldAlt,
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  Line,
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { SectionLabel } from './DashboardWrapper';
import {
  calculateRevenueOpportunity,
  getLatestRevenueOpportunity,
  getRevenueOpportunityHistory,
} from '../../services/revenueOpportunityService';
import { logger } from '../../utils/logger';

// --------------------------------------------------------------------------
// Formatting helpers
// --------------------------------------------------------------------------

const fmtCurrency = (value, precise = false) => {
  if (value == null || isNaN(value)) return '$0';
  if (!precise) {
    if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${Math.round(value).toLocaleString()}`;
};

const fmtNumber = (value, digits = 0) => {
  if (value == null || isNaN(value)) return '0';
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: digits,
  });
};

const fmtPct = (value) => {
  if (value == null || isNaN(value)) return '0%';
  return `${Number(value).toFixed(1)}%`;
};

const SCENARIO_LABELS = {
  maintain_current_share: 'Maintain current share',
  median_of_top_five: 'Reach top-five median',
  reach_10pct_market_share: 'Reach 10% market share',
  reach_next_competitor: 'Reach next competitor',
  reach_requested_target: 'Reach your revenue target',
};

const CHART_COLORS = ['#2F80ED', '#27AE60', '#EB5757', '#9B51E0', '#F2994A', '#219EBC'];

// Data-provenance model. Keeping observed public data, third-party estimates,
// modeled estimates, customer facts and recommendations visually distinct is a
// hard requirement of this feature.
const PROVENANCE = {
  customer: { label: 'Your facts', color: 'blue' },
  observed: { label: 'Observed public data', color: 'green' },
  third_party: { label: 'Third-party estimate', color: 'orange' },
  modeled: { label: 'Localytics modeled estimate', color: 'purple' },
  recommendation: { label: 'Recommendation', color: 'teal' },
};

const Provenance = ({ kind }) => {
  const p = PROVENANCE[kind] || PROVENANCE.modeled;
  return (
    <Tag size="sm" colorScheme={p.color} variant="subtle" borderRadius="full">
      <TagLabel fontSize="2xs" textTransform="uppercase" letterSpacing="wide">
        {p.label}
      </TagLabel>
    </Tag>
  );
};

// --------------------------------------------------------------------------
// Small presentational building blocks
// --------------------------------------------------------------------------

const SectionCard = ({ title, icon, provenance, children, testId, ...rest }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      shadow="sm"
      data-testid={testId}
      {...rest}
    >
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center" gap={3} wrap="wrap">
          <HStack spacing={3}>
            {icon && <Icon as={icon} color="brand.500" boxSize={5} />}
            <Heading size="sm">{title}</Heading>
          </HStack>
          {provenance && (
            <Wrap spacing={1}>
              {(Array.isArray(provenance) ? provenance : [provenance]).map((p) => (
                <WrapItem key={p}>
                  <Provenance kind={p} />
                </WrapItem>
              ))}
            </Wrap>
          )}
        </Flex>
      </CardHeader>
      <CardBody pt={2}>{children}</CardBody>
    </Card>
  );
};

const StatTile = ({ label, value, help, color = 'gray.800', testId }) => {
  const bg = useColorModeValue('gray.50', 'gray.700');
  return (
    <Stat bg={bg} p={4} borderRadius="lg" data-testid={testId}>
      <StatLabel fontSize="xs" color="gray.500">
        {label}
      </StatLabel>
      <StatNumber fontSize="xl" color={color}>
        {value}
      </StatNumber>
      {help && <StatHelpText fontSize="xs" mb={0}>{help}</StatHelpText>}
    </Stat>
  );
};

const confidenceColor = (level) =>
  level === 'high' ? 'green' : level === 'medium' ? 'yellow' : 'red';

// --------------------------------------------------------------------------
// Input form
// --------------------------------------------------------------------------

const REQUIRED_FIELDS = [
  { key: 'avg_daily_revenue', label: 'Average daily revenue', prefix: '$', required: true },
  { key: 'avg_daily_customers', label: 'Average daily customers / jobs', required: true },
  { key: 'working_days_per_month', label: 'Working days per month', required: true },
  { key: 'target_monthly_revenue', label: 'Desired monthly revenue target', prefix: '$', required: true },
];

const OPTIONAL_FIELDS = [
  { key: 'monthly_ad_spend', label: 'Current monthly ad spend', prefix: '$' },
  { key: 'lead_to_booking_rate', label: 'Lead-to-booking rate (0-1)', step: '0.01' },
  { key: 'capacity_per_day', label: 'Current capacity (jobs/day)' },
  { key: 'commercial_revenue_pct', label: '% revenue from commercial/recurring' },
  { key: 'service_area', label: 'Primary service area', text: true },
];

const InputForm = ({ businesses, initialInputs, initialCustomer, onSubmit, submitting }) => {
  const { isOpen: showOptional, onToggle: toggleOptional } = useDisclosure();
  const [values, setValues] = useState(() => ({
    avg_daily_revenue: '',
    avg_daily_customers: '',
    working_days_per_month: '22',
    target_monthly_revenue: '',
    monthly_ad_spend: '',
    lead_to_booking_rate: '',
    capacity_per_day: '',
    commercial_revenue_pct: '',
    service_area: '',
    ...(initialInputs || {}),
  }));
  const [customer, setCustomer] = useState(initialCustomer || '');

  const setField = (key) => (e) =>
    setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const requiredComplete = REQUIRED_FIELDS.every(
    (f) => values[f.key] !== '' && values[f.key] != null
  );

  const handleSubmit = () => {
    const inputs = {};
    [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS].forEach((f) => {
      const raw = values[f.key];
      if (raw === '' || raw == null) return;
      inputs[f.key] = f.text ? raw : Number(raw);
    });
    onSubmit({ inputs, customerName: customer || null });
  };

  const renderField = (f) => (
    <FormControl key={f.key} isRequired={f.required}>
      <FormLabel fontSize="sm" mb={1}>
        {f.label}
      </FormLabel>
      {f.prefix ? (
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none" color="gray.400">
            {f.prefix}
          </InputLeftElement>
          <Input
            type="number"
            value={values[f.key]}
            onChange={setField(f.key)}
            data-testid={`input-${f.key}`}
          />
        </InputGroup>
      ) : (
        <Input
          size="sm"
          type={f.text ? 'text' : 'number'}
          step={f.step}
          value={values[f.key]}
          onChange={setField(f.key)}
          data-testid={`input-${f.key}`}
        />
      )}
    </FormControl>
  );

  return (
    <SectionCard title="Enter your numbers" icon={FaCalculator} provenance="customer" testId="revopp-input-form">
      <Text fontSize="sm" color="gray.500" mb={4}>
        Just four numbers get you a full modeled report. Optional fields sharpen
        the estimate and raise the confidence score.
      </Text>

      <FormControl mb={4}>
        <FormLabel fontSize="sm" mb={1}>
          Which of the tracked businesses is yours?
        </FormLabel>
        <Select
          size="sm"
          placeholder="Select your business (defaults to top-ranked)"
          value={customer}
          onChange={(e) => setCustomer(e.target.value)}
          data-testid="input-customer-business"
        >
          {businesses.map((b) => (
            <option key={b.maps_uri || b.name} value={b.name}>
              {b.name}
            </option>
          ))}
        </Select>
        <FormHelperText fontSize="xs">
          Used to calibrate the model against your real revenue.
        </FormHelperText>
      </FormControl>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {REQUIRED_FIELDS.map(renderField)}
      </SimpleGrid>

      <Button
        variant="link"
        size="sm"
        mt={4}
        colorScheme="blue"
        onClick={toggleOptional}
        data-testid="toggle-optional"
      >
        {showOptional ? 'Hide' : 'Show'} optional inputs
      </Button>
      <Collapse in={showOptional} animateOpacity>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={3}>
          {OPTIONAL_FIELDS.map(renderField)}
        </SimpleGrid>
      </Collapse>

      <Flex mt={5} justify="flex-end">
        <Button
          colorScheme="blue"
          leftIcon={<FaChartLine />}
          onClick={handleSubmit}
          isLoading={submitting}
          isDisabled={!requiredComplete}
          data-testid="calculate-button"
        >
          Build my Revenue Opportunity report
        </Button>
      </Flex>
    </SectionCard>
  );
};

// --------------------------------------------------------------------------
// Report sections
// --------------------------------------------------------------------------

const DisclaimerBanner = ({ disclaimers }) => (
  <Alert
    status="info"
    variant="left-accent"
    borderRadius="lg"
    flexDirection="column"
    alignItems="flex-start"
    data-testid="revopp-disclaimers"
  >
    <HStack mb={2}>
      <AlertIcon />
      <AlertTitle>These figures are modeled estimates — not verified financial data.</AlertTitle>
    </HStack>
    <AlertDescription>
      <List spacing={1} fontSize="sm">
        {(disclaimers || []).map((d, i) => (
          <ListItem key={i}>
            <ListIcon as={FaInfoCircle} color="blue.400" />
            {d}
          </ListItem>
        ))}
      </List>
    </AlertDescription>
  </Alert>
);

const ProvenanceLegend = () => (
  <Wrap spacing={2} data-testid="provenance-legend">
    {Object.keys(PROVENANCE).map((k) => (
      <WrapItem key={k}>
        <Provenance kind={k} />
      </WrapItem>
    ))}
  </Wrap>
);

const RevenueSnapshot = ({ metrics, customer }) => (
  <SectionCard title="1. Revenue snapshot" icon={FaDollarSign} provenance={['customer', 'modeled']} testId="section-snapshot">
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
      <StatTile label="Estimated monthly revenue" value={fmtCurrency(metrics.monthly_revenue, true)} color="green.500" testId="snapshot-monthly" />
      <StatTile label="Estimated annualized revenue" value={fmtCurrency(metrics.annualized_revenue)} />
      <StatTile label="Average ticket" value={fmtCurrency(metrics.average_ticket, true)} />
      <StatTile label="Monthly customers" value={fmtNumber(metrics.monthly_customers)} />
    </SimpleGrid>
    {metrics.warnings && metrics.warnings.length > 0 && (
      <VStack align="stretch" mt={3} spacing={1}>
        {metrics.warnings.map((w, i) => (
          <Alert key={i} status="warning" size="sm" borderRadius="md" py={1}>
            <AlertIcon />
            <Text fontSize="xs">{w}</Text>
          </Alert>
        ))}
      </VStack>
    )}
  </SectionCard>
);

const MarketSize = ({ marketModel, concentration }) => {
  const total = marketModel.total_local_market_revenue || {};
  const data = [
    { name: 'Conservative', value: total.conservative },
    { name: 'Expected', value: total.expected },
    { name: 'Aggressive', value: total.aggressive },
  ];
  return (
    <SectionCard title="2. Estimated market size" icon={FaGlobe} provenance="modeled" testId="section-market-size">
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        <StatTile label="Conservative" value={fmtCurrency(total.conservative)} help="per month" />
        <StatTile label="Expected" value={fmtCurrency(total.expected)} help="per month" color="purple.500" />
        <StatTile label="Aggressive" value={fmtCurrency(total.aggressive)} help="per month" />
      </SimpleGrid>
      <Box height="220px">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={(v) => fmtCurrency(v)} />
            <RechartsTooltip formatter={(v) => fmtCurrency(v, true)} />
            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={index} fill={CHART_COLORS[index]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Text fontSize="sm" color="gray.500" mt={3}>
        Market classified as <b>{concentration.bucket}</b> (top-5 control{' '}
        {fmtPct(concentration.top5_share * 100)}). An untracked-market adjustment of{' '}
        {fmtPct(marketModel.untracked_adjustment.low_pct * 100)}–
        {fmtPct(marketModel.untracked_adjustment.high_pct * 100)} was added because the
        20 tracked competitors do not represent every business or revenue source.
        {concentration.franchise_present && ' Large franchises appear to be present.'}
      </Text>
    </SectionCard>
  );
};

const MarketShareRange = ({ share, rank }) => {
  const total = share.total_local_revenue_market_share || {};
  const tracked = share.share_among_tracked_competitors || {};
  return (
    <SectionCard title="3. Market-share range" icon={FaChartPie} provenance="modeled" testId="section-market-share">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>{total.label}</Text>
          <Heading size="lg" color="purple.500" data-testid="market-share-range">
            {fmtPct(total.low_pct)}–{fmtPct(total.high_pct)}
          </Heading>
          <Text fontSize="xs" color="gray.500">Midpoint {fmtPct(total.mid_pct)}</Text>
        </Box>
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>{tracked.label}</Text>
          <Heading size="lg" color="blue.500">{fmtPct(tracked.index_based_pct)}</Heading>
          <Text fontSize="xs" color="gray.500">
            Revenue-based {fmtPct(tracked.revenue_based_pct)}
          </Text>
        </Box>
      </SimpleGrid>
    </SectionCard>
  );
};

const RevenueRank = ({ rank, competitors }) => {
  const data = [...(competitors || [])]
    .sort((a, b) => b.relative_revenue_index - a.relative_revenue_index)
    .map((c) => ({
      name: c.is_customer ? `${c.name} (you)` : c.name,
      index: c.relative_revenue_index,
      isCustomer: c.is_customer,
    }));
  return (
    <SectionCard title="4. Revenue rank among 21 businesses" icon={FaTrophy} provenance="modeled" testId="section-rank">
      <HStack mb={3}>
        <Badge colorScheme="purple" fontSize="md" px={3} py={1} borderRadius="full">
          Estimated rank #{rank.rank} of {rank.total}
        </Badge>
      </HStack>
      <Box height={`${Math.max(240, data.length * 22)}px`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis type="number" />
            <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
            <RechartsTooltip />
            <Bar dataKey="index" radius={[0, 6, 6, 0]}>
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.isCustomer ? '#EB5757' : '#2F80ED'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </SectionCard>
  );
};

const CustomerVolume = ({ share, metrics }) => {
  const v = share.customer_volume_market_share || {};
  return (
    <SectionCard title="5. Customer-volume analysis" icon={FaUsers} provenance="modeled" testId="section-volume">
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <StatTile label="Your monthly customers" value={fmtNumber(metrics.monthly_customers)} />
        <StatTile label="Est. market customers" value={`${fmtNumber(v.estimated_market_monthly_customers_low)}–${fmtNumber(v.estimated_market_monthly_customers_high)}`} help="per month" />
        <StatTile label="Volume market share" value={`${fmtPct(v.low_pct)}–${fmtPct(v.high_pct)}`} color="purple.500" />
        <StatTile label="Assumed competitor ticket" value={`${fmtCurrency(v.assumed_competitor_ticket_low, true)}–${fmtCurrency(v.assumed_competitor_ticket_high, true)}`} />
      </SimpleGrid>
      <Text fontSize="xs" color="gray.500" mt={3}>
        Competitor customer counts are modeled from estimated revenue divided by an
        assumed average ticket derived from your own ticket and category positioning.
        Shown as a range because competitor tickets are not directly observable.
      </Text>
    </SectionCard>
  );
};

const CompetitiveRevenueDistribution = ({ competitors }) => {
  const data = [...(competitors || [])]
    .sort((a, b) => b.modeled_monthly_revenue - a.modeled_monthly_revenue)
    .map((c) => ({
      name: c.is_customer ? `${c.name} (you)` : c.name,
      revenue: c.modeled_monthly_revenue,
      low: c.conservative_monthly_revenue,
      high: c.aggressive_monthly_revenue,
      isCustomer: c.is_customer,
    }));
  return (
    <SectionCard title="6. Competitive revenue distribution" icon={FaChartBar} provenance="modeled" testId="section-distribution">
      <Text fontSize="xs" color="gray.500" mb={3}>
        Estimated monthly revenue per tracked business (your row uses your real
        self-reported revenue; all others are modeled).
      </Text>
      <Box height={`${Math.max(240, data.length * 24)}px`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis type="number" tickFormatter={(v) => fmtCurrency(v)} />
            <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
            <RechartsTooltip formatter={(v) => fmtCurrency(v, true)} />
            <Bar dataKey="revenue" radius={[0, 6, 6, 0]}>
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.isCustomer ? '#27AE60' : '#9B51E0'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </SectionCard>
  );
};

const OpportunityScenarios = ({ opportunity }) => {
  const scenarios = opportunity.scenarios || [];
  const chartData = scenarios.map((s) => ({
    name: SCENARIO_LABELS[s.key] || s.key,
    target: s.target_monthly_revenue,
    additional: s.additional_monthly_revenue,
  }));
  return (
    <SectionCard title="7. Revenue opportunity scenarios" icon={FaChartLine} provenance="modeled" testId="section-opportunity">
      <Box height="260px" mb={4}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" angle={-20} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
            <YAxis tickFormatter={(v) => fmtCurrency(v)} />
            <RechartsTooltip formatter={(v) => fmtCurrency(v, true)} />
            <Legend />
            <Bar dataKey="target" name="Target revenue" fill="#2F80ED" radius={[6, 6, 0, 0]} />
            <Bar dataKey="additional" name="Additional required" fill="#F2994A" radius={[6, 6, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <TableContainer>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Scenario</Th>
              <Th isNumeric>Target /mo</Th>
              <Th isNumeric>Add&apos;l /mo</Th>
              <Th isNumeric>Add&apos;l customers</Th>
              <Th isNumeric>Add&apos;l /day</Th>
              <Th isNumeric>Share after</Th>
            </Tr>
          </Thead>
          <Tbody>
            {scenarios.map((s) => (
              <Tr key={s.key}>
                <Td>
                  {SCENARIO_LABELS[s.key] || s.key}
                  {s.exceeds_estimated_market_capacity && (
                    <Tooltip label={s.warning}>
                      <span> <Icon as={FaExclamationTriangle} color="orange.400" /></span>
                    </Tooltip>
                  )}
                </Td>
                <Td isNumeric>{fmtCurrency(s.target_monthly_revenue)}</Td>
                <Td isNumeric>{fmtCurrency(s.additional_monthly_revenue)}</Td>
                <Td isNumeric>{fmtNumber(s.additional_monthly_customers)}</Td>
                <Td isNumeric>{fmtNumber(s.additional_daily_customers, 1)}</Td>
                <Td isNumeric>{fmtPct(s.estimated_market_share_after_pct)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </SectionCard>
  );
};

const GapComparisonChart = ({ current, median, top5, label }) => {
  const data = [
    { name: 'You', value: current, fill: '#EB5757' },
    { name: 'Market median', value: median, fill: '#2F80ED' },
    { name: 'Top-five median', value: top5, fill: '#27AE60' },
  ];
  return (
    <Box height="180px">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <RechartsTooltip formatter={(v) => fmtNumber(v, 1)} />
          <Bar dataKey="value" name={label} radius={[6, 6, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={index} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const ReviewGap = ({ gap }) => (
  <SectionCard title="8. Review gap" icon={FaStar} provenance="observed" testId="section-review-gap">
    <GapComparisonChart
      current={gap.current_review_velocity}
      median={gap.market_median_velocity}
      top5={gap.top_five_median_velocity}
      label="Reviews / month"
    />
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mt={3}>
      <StatTile label="Your review total" value={fmtNumber(gap.current_review_total)} />
      <StatTile label="Reviews to close gap" value={fmtNumber(gap.reviews_to_close_gap)} />
      <StatTile label="Velocity for 12mo" value={`${fmtNumber(gap.required_velocity_12mo, 1)}/mo`} />
      <StatTile label="Velocity for 6mo" value={`${fmtNumber(gap.required_velocity_6mo, 1)}/mo`} />
    </SimpleGrid>
    <Text fontSize="xs" color="gray.500" mt={3}>{gap.note}</Text>
  </SectionCard>
);

const TrafficGap = ({ gap }) => (
  <SectionCard title="9. Website traffic gap" icon={FaGlobe} provenance="third_party" testId="section-traffic-gap">
    <GapComparisonChart
      current={gap.current_monthly_traffic}
      median={gap.market_median_traffic}
      top5={gap.top_five_median_traffic}
      label="Monthly visits"
    />
    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mt={3}>
      <StatTile label="Your monthly traffic" value={fmtNumber(gap.current_monthly_traffic)} />
      <StatTile label="Traffic to match next" value={fmtNumber(gap.additional_traffic_to_match_next)} />
      <StatTile label="Est. added customers" value={fmtNumber(gap.estimated_additional_customers_from_next_traffic, 1)} help="from that traffic" />
    </SimpleGrid>
    <Text fontSize="xs" color="gray.500" mt={3}>
      Assumes {fmtPct(gap.assumptions.website_conversion_rate * 100)} visit-to-lead and{' '}
      {fmtPct(gap.assumptions.lead_to_booking_rate * 100)} booking rate. {gap.note}
    </Text>
  </SectionCard>
);

const PpcGap = ({ gap }) => {
  const outcome = gap.estimated_revenue_outcome || {};
  const leads = gap.estimated_incremental_leads || {};
  return (
    <SectionCard title="10. PPC gap" icon={FaBullhorn} provenance={['third_party', 'modeled']} testId="section-ppc-gap">
      <HStack mb={3} spacing={4} wrap="wrap">
        <Badge colorScheme={gap.customer_runs_ppc ? 'green' : 'red'}>
          {gap.customer_runs_ppc ? 'You run Google PPC' : 'You do not run Google PPC'}
        </Badge>
        <Badge colorScheme="blue">
          {gap.competitors_running_ppc} competitors ({fmtPct(gap.competitors_running_ppc_pct)}) run PPC
        </Badge>
      </HStack>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <StatTile label="Break-even CAC" value={fmtCurrency(gap.break_even_cac, true)} />
        <StatTile label="Max recommended CAC" value={fmtCurrency(gap.max_recommended_cac, true)} />
        <StatTile label="Proposed budget" value={fmtCurrency(gap.assumptions.proposed_monthly_budget)} help="modeled" />
        <StatTile label="Required booked jobs" value={fmtNumber(gap.required_booked_jobs, 1)} />
      </SimpleGrid>
      <Text fontSize="sm" fontWeight="semibold" mt={4} mb={1}>Estimated incremental outcome</Text>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
        <StatTile label="Conservative" value={fmtCurrency(outcome.conservative)} help={`${fmtNumber(leads.conservative, 1)} leads`} />
        <StatTile label="Expected" value={fmtCurrency(outcome.expected)} help={`${fmtNumber(leads.expected, 1)} leads`} color="purple.500" />
        <StatTile label="Aggressive" value={fmtCurrency(outcome.aggressive)} help={`${fmtNumber(leads.aggressive, 1)} leads`} />
      </SimpleGrid>
      <Text fontSize="xs" color="gray.500" mt={3}>{gap.note}</Text>
    </SectionCard>
  );
};

const TicketVolumeLevers = ({ ticketGap, volumeGap }) => (
  <SectionCard title="11. Average-ticket & customer-volume levers" icon={FaCalculator} provenance="modeled" testId="section-levers">
    <Text fontSize="sm" fontWeight="semibold" mb={2}>Average-ticket lever</Text>
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={4}>
      <StatTile label="+5% ticket" value={fmtCurrency(ticketGap.revenue_if_ticket_up_5pct)} help="est. monthly revenue" />
      <StatTile label="+10% ticket" value={fmtCurrency(ticketGap.revenue_if_ticket_up_10pct)} help="est. monthly revenue" />
      <StatTile label="+20% ticket" value={fmtCurrency(ticketGap.revenue_if_ticket_up_20pct)} help="est. monthly revenue" />
    </SimpleGrid>
    <Text fontSize="xs" color="gray.500" mb={3}>
      Current ticket {fmtCurrency(ticketGap.current_average_ticket, true)} vs modeled category range{' '}
      {fmtCurrency(ticketGap.modeled_category_ticket_low, true)}–{fmtCurrency(ticketGap.modeled_category_ticket_high, true)}. {ticketGap.note}
    </Text>
    <Divider my={3} />
    <Text fontSize="sm" fontWeight="semibold" mb={2}>Customer-volume lever</Text>
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
      <StatTile label="Add'l monthly customers" value={fmtNumber(volumeGap.additional_monthly_customers)} />
      <StatTile label="Add'l daily customers" value={fmtNumber(volumeGap.additional_daily_customers, 1)} />
      <StatTile label="Target daily customers" value={fmtNumber(volumeGap.target_daily_customers, 1)} />
    </SimpleGrid>
  </SectionCard>
);

const CapacityRequirements = ({ capacityGap }) => (
  <SectionCard title="12. Capacity requirements" icon={FaClipboardList} provenance={['customer', 'modeled']} testId="section-capacity">
    {capacityGap ? (
      <>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <StatTile label="Current capacity" value={`${fmtNumber(capacityGap.current_capacity_per_day, 1)}/day`} />
          <StatTile label="Current utilization" value={fmtPct(capacityGap.current_utilization_pct)} />
          <StatTile label="Required at target" value={`${fmtNumber(capacityGap.required_capacity_at_target, 1)}/day`} />
          <StatTile label="Additional needed" value={`${fmtNumber(capacityGap.additional_capacity_required, 1)}/day`} color="orange.500" />
        </SimpleGrid>
        <Progress
          mt={3}
          value={Math.min(100, capacityGap.current_utilization_pct)}
          colorScheme={capacityGap.current_utilization_pct > 85 ? 'red' : 'blue'}
          borderRadius="full"
        />
        <Text fontSize="xs" color="gray.500" mt={3}>{capacityGap.note}</Text>
      </>
    ) : (
      <Text fontSize="sm" color="gray.500">
        Provide your current operational capacity (jobs/day) in the optional inputs
        to model capacity requirements at your target revenue.
      </Text>
    )}
  </SectionCard>
);

const Blueprint = ({ blueprint }) => {
  const cp = blueprint.current_position || {};
  const rt = blueprint.revenue_target || {};
  const plan = blueprint.ninety_day_plan || {};
  const planCols = [
    { title: 'First 30 days', items: plan.first_30_days },
    { title: 'Days 31–60', items: plan.days_31_60 },
    { title: 'Days 61–90', items: plan.days_61_90 },
  ];
  return (
    <SectionCard title={`13. ${blueprint.title}`} icon={FaFlagCheckered} provenance="recommendation" testId="section-blueprint">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={2}>Current position</Text>
          <VStack align="stretch" spacing={1} fontSize="sm">
            <HStack justify="space-between"><Text color="gray.500">Monthly revenue</Text><Text>{fmtCurrency(cp.estimated_monthly_revenue, true)}</Text></HStack>
            <HStack justify="space-between"><Text color="gray.500">Annualized</Text><Text>{fmtCurrency(cp.estimated_annualized_revenue)}</Text></HStack>
            <HStack justify="space-between"><Text color="gray.500">Average ticket</Text><Text>{fmtCurrency(cp.average_ticket, true)}</Text></HStack>
            <HStack justify="space-between"><Text color="gray.500">Monthly customers</Text><Text>{fmtNumber(cp.monthly_customers)}</Text></HStack>
            <HStack justify="space-between"><Text color="gray.500">Market-share range</Text><Text>{fmtPct(cp.estimated_market_share_range_pct?.low)}–{fmtPct(cp.estimated_market_share_range_pct?.high)}</Text></HStack>
            <Text color="gray.500" fontSize="xs" mt={1}>{cp.estimated_revenue_rank}</Text>
          </VStack>
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={2}>Revenue target</Text>
          <VStack align="stretch" spacing={1} fontSize="sm">
            <HStack justify="space-between"><Text color="gray.500">Desired monthly revenue</Text><Text>{fmtCurrency(rt.desired_monthly_revenue, true)}</Text></HStack>
            <HStack justify="space-between"><Text color="gray.500">Additional revenue</Text><Text>{fmtCurrency(rt.additional_revenue_required, true)}</Text></HStack>
            <HStack justify="space-between"><Text color="gray.500">Additional monthly customers</Text><Text>{fmtNumber(rt.additional_monthly_customers_required)}</Text></HStack>
            <HStack justify="space-between"><Text color="gray.500">Additional daily customers</Text><Text>{fmtNumber(rt.additional_daily_customers_required, 1)}</Text></HStack>
            <HStack justify="space-between"><Text color="gray.500">Target market share</Text><Text>{fmtPct(rt.target_estimated_market_share_pct)}</Text></HStack>
          </VStack>
        </Box>
      </SimpleGrid>

      <Text fontSize="sm" fontWeight="semibold" mb={2}>Top growth constraints</Text>
      <VStack align="stretch" spacing={2} mb={4}>
        {(blueprint.top_growth_constraints || []).map((c, i) => (
          <Alert key={i} status="warning" borderRadius="md" py={2}>
            <AlertIcon as={FaExclamationTriangle} />
            <Box>
              <Text fontSize="sm" fontWeight="semibold">{c.constraint}</Text>
              <Text fontSize="xs" color="gray.600">{c.detail}</Text>
            </Box>
          </Alert>
        ))}
      </VStack>

      <Text fontSize="sm" fontWeight="semibold" mb={2}>Recommended 90-day plan</Text>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        {planCols.map((col) => (
          <Box key={col.title} p={3} borderWidth="1px" borderColor="gray.200" borderRadius="lg">
            <Text fontSize="sm" fontWeight="bold" color="brand.500" mb={2}>{col.title}</Text>
            <List spacing={2} fontSize="sm">
              {(col.items || []).map((item, i) => (
                <ListItem key={i}>
                  <ListIcon as={FaCheckCircle} color="green.400" />
                  {item}
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </SimpleGrid>
    </SectionCard>
  );
};

const Forecasts = ({ forecasts }) => {
  const order = ['conservative', 'expected', 'aggressive'];
  const chartData = order.map((k) => ({
    name: forecasts[k]?.scenario || k,
    revenue: forecasts[k]?.estimated_monthly_revenue,
    customers: forecasts[k]?.estimated_monthly_customers,
  }));
  return (
    <SectionCard title="Scenario forecasting" icon={FaChartLine} provenance="modeled" testId="section-forecasts">
      <Box height="240px" mb={4}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" />
            <YAxis yAxisId="left" tickFormatter={(v) => fmtCurrency(v)} />
            <YAxis yAxisId="right" orientation="right" />
            <RechartsTooltip formatter={(v, n) => (n === 'revenue' ? fmtCurrency(v, true) : fmtNumber(v))} />
            <Legend />
            <Bar yAxisId="left" dataKey="revenue" name="Est. monthly revenue" fill="#2F80ED" radius={[6, 6, 0, 0]} />
            <Line yAxisId="right" dataKey="customers" name="Est. monthly customers" stroke="#EB5757" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        {order.map((k) => {
          const f = forecasts[k] || {};
          return (
            <Box key={k} p={3} borderWidth="1px" borderColor="gray.200" borderRadius="lg">
              <Text fontWeight="bold" mb={2}>{f.scenario}</Text>
              <VStack align="stretch" spacing={1} fontSize="sm">
                <HStack justify="space-between"><Text color="gray.500">Revenue</Text><Text>{fmtCurrency(f.estimated_monthly_revenue, true)}</Text></HStack>
                <HStack justify="space-between"><Text color="gray.500">Customers</Text><Text>{fmtNumber(f.estimated_monthly_customers)}</Text></HStack>
                <HStack justify="space-between"><Text color="gray.500">Avg ticket</Text><Text>{fmtCurrency(f.estimated_average_ticket, true)}</Text></HStack>
                <HStack justify="space-between"><Text color="gray.500">Ad spend</Text><Text>{fmtCurrency(f.estimated_ad_spend)}</Text></HStack>
                <HStack justify="space-between"><Text color="gray.500">Market share</Text><Text>{fmtPct(f.estimated_market_share_pct)}</Text></HStack>
                <HStack justify="space-between"><Text color="gray.500">Capacity/day</Text><Text>{fmtNumber(f.operational_capacity_required_per_day, 1)}</Text></HStack>
              </VStack>
              <Divider my={2} />
              <List spacing={0.5} fontSize="xs" color="gray.500">
                {(f.assumptions || []).map((a, i) => (
                  <ListItem key={i}>• {a}</ListItem>
                ))}
              </List>
            </Box>
          );
        })}
      </SimpleGrid>
      <Alert status="warning" mt={3} borderRadius="md" py={2}>
        <AlertIcon />
        <Text fontSize="sm">{forecasts.disclaimer}</Text>
      </Alert>
    </SectionCard>
  );
};

const ConfidenceCard = ({ confidence }) => (
  <SectionCard title="14. Model confidence & assumptions" icon={FaShieldAlt} provenance="modeled" testId="section-confidence">
    <HStack spacing={4} mb={3}>
      <Box position="relative" textAlign="center">
        <Heading size="2xl" color={`${confidenceColor(confidence.level)}.500`} data-testid="confidence-score">
          {fmtNumber(confidence.score)}
        </Heading>
        <Badge colorScheme={confidenceColor(confidence.level)} textTransform="capitalize">
          {confidence.level} confidence
        </Badge>
      </Box>
      <Box flex="1">
        <Progress
          value={confidence.score}
          colorScheme={confidenceColor(confidence.level)}
          borderRadius="full"
          size="lg"
        />
        <Text fontSize="sm" color="gray.600" mt={2}>{confidence.summary}</Text>
      </Box>
    </HStack>
    <Text fontSize="sm" fontWeight="semibold" mt={2} mb={1}>Primary sources of uncertainty</Text>
    <List spacing={1} fontSize="sm">
      {(confidence.primary_uncertainties || []).map((u, i) => (
        <ListItem key={i}>
          <ListIcon as={FaInfoCircle} color="orange.400" />
          {u}
        </ListItem>
      ))}
    </List>
  </SectionCard>
);

const HistoryTracking = ({ trend }) => {
  if (!trend || trend.length < 2) return null;
  const data = trend.map((t) => ({
    date: new Date(t.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    revenue: t.monthly_revenue,
    shareMid: (t.market_share_low_pct + t.market_share_high_pct) / 2,
    customers: t.monthly_customers,
  }));
  return (
    <SectionCard title="Historical tracking" icon={FaChartLine} provenance="modeled" testId="section-history">
      <Text fontSize="xs" color="gray.500" mb={3}>
        Recalculated over time as your numbers and competitor data change.
      </Text>
      <Box height="240px">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="date" />
            <YAxis yAxisId="left" tickFormatter={(v) => fmtCurrency(v)} />
            <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} />
            <RechartsTooltip />
            <Legend />
            <Area yAxisId="left" type="monotone" dataKey="revenue" name="Monthly revenue" fill="#2F80ED" stroke="#2F80ED" fillOpacity={0.15} />
            <Line yAxisId="right" type="monotone" dataKey="shareMid" name="Market share %" stroke="#9B51E0" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </SectionCard>
  );
};

// --------------------------------------------------------------------------
// Main dashboard
// --------------------------------------------------------------------------

const RevenueOpportunityDashboard = ({ reviewsData = [], sectionLabel }) => {
  const [report, setReport] = useState(null);
  const [trend, setTrend] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [lastInputs, setLastInputs] = useState(null);
  const [lastCustomer, setLastCustomer] = useState(null);

  const businesses = useMemo(
    () =>
      (reviewsData || [])
        .filter((b) => b && b.name)
        .map((b) => ({ name: b.name, maps_uri: b.maps_uri })),
    [reviewsData]
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      setInitialLoading(true);
      try {
        const [latest, history] = await Promise.all([
          getLatestRevenueOpportunity().catch(() => null),
          getRevenueOpportunityHistory().catch(() => null),
        ]);
        if (!active) return;
        if (latest && latest.success && latest.report && Object.keys(latest.report).length) {
          setReport(latest.report);
        }
        if (history && history.trend) {
          setTrend(history.trend);
        }
      } catch (err) {
        logger.error('Error loading revenue opportunity:', err);
      } finally {
        if (active) setInitialLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const handleCalculate = useCallback(async ({ inputs, customerName }) => {
    setSubmitting(true);
    setError(null);
    try {
      const resp = await calculateRevenueOpportunity({ inputs, customerName });
      if (resp && resp.report) {
        setReport(resp.report);
        setLastInputs(inputs);
        setLastCustomer(customerName);
        const history = await getRevenueOpportunityHistory().catch(() => null);
        if (history && history.trend) setTrend(history.trend);
      } else {
        setError('The report could not be generated.');
      }
    } catch (err) {
      logger.error('Error calculating revenue opportunity:', err);
      setError(err.message || 'Failed to calculate revenue opportunity');
    } finally {
      setSubmitting(false);
    }
  }, []);

  if (initialLoading) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.500">Loading your Revenue Opportunity model…</Text>
        </VStack>
      </Flex>
    );
  }

  const hasReport = report && report.customer_metrics && !report.error;

  return (
    <Box data-testid="revenue-opportunity-dashboard">
      {sectionLabel && (
        <SectionLabel description="Estimate the size of your local market, your likely share of it, and exactly what it takes to hit your revenue goal.">
          {sectionLabel}
        </SectionLabel>
      )}

      <VStack align="stretch" spacing={5}>
        <Alert status="info" borderRadius="lg" variant="subtle">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Local Revenue Opportunity Model</AlertTitle>
            <AlertDescription fontSize="sm">
              Localytics estimates the size of your local market, shows your likely
              share of it, and calculates the customers, traffic, reviews and
              advertising performance required to reach your revenue goal.
            </AlertDescription>
          </Box>
        </Alert>

        <InputForm
          businesses={businesses}
          initialInputs={lastInputs}
          initialCustomer={lastCustomer}
          onSubmit={handleCalculate}
          submitting={submitting}
        />

        {error && (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {hasReport ? (
          <>
            <Card variant="outline" borderRadius="lg">
              <CardBody>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                  How to read the colors
                </Text>
                <ProvenanceLegend />
              </CardBody>
            </Card>

            <RevenueSnapshot metrics={report.customer_metrics} customer={report.customer} />
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
              <MarketSize marketModel={report.model_b_market_model} concentration={report.market_concentration} />
              <MarketShareRange share={report.market_share} rank={report.revenue_rank} />
            </SimpleGrid>
            <RevenueRank rank={report.revenue_rank} competitors={report.competitor_signals} />
            <CustomerVolume share={report.market_share} metrics={report.customer_metrics} />
            <CompetitiveRevenueDistribution competitors={report.model_b_market_model.competitor_estimates} />
            <OpportunityScenarios opportunity={report.revenue_opportunity} />
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
              <ReviewGap gap={report.growth_gaps.review_gap} />
              <TrafficGap gap={report.growth_gaps.traffic_gap} />
            </SimpleGrid>
            <PpcGap gap={report.growth_gaps.ppc_gap} />
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
              <TicketVolumeLevers ticketGap={report.growth_gaps.average_ticket_gap} volumeGap={report.growth_gaps.customer_volume_gap} />
              <CapacityRequirements capacityGap={report.growth_gaps.capacity_gap} />
            </SimpleGrid>
            <Blueprint blueprint={report.blueprint} />
            <Forecasts forecasts={report.forecasts} />
            <HistoryTracking trend={trend} />
            <ConfidenceCard confidence={report.confidence} />
            <DisclaimerBanner disclaimers={report.disclaimers} />
          </>
        ) : (
          <Card variant="outline" borderRadius="lg">
            <CardBody>
              <VStack spacing={3} py={8}>
                <Icon as={FaLightbulb} boxSize={8} color="yellow.400" />
                <Text color="gray.500" textAlign="center">
                  Enter your numbers above to generate your Local Revenue
                  Opportunity report.
                </Text>
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
};

export default RevenueOpportunityDashboard;
